import { Api } from "../../Api";
import { DataFrame, Method } from "../../DataFrame";
import { Helpers, Task } from "../../Helpers";
import { IDIService } from "../../IDIService";
import { ExalusConnectionService } from "../ExalusConnectionService";
import { IExalusConnectionService } from "../IExalusConnectionService";
import { LocalStorageService } from "../LocalStorageService";
import { ILoggerService } from "../Logging/ILoggerService";
import { LoggerService } from "../Logging/LoggerService";
import { ISessionService } from "../Session/ISessionService";
import { SessionService } from "../Session/SessionService";

export interface IFindControllerService extends IDIService {
	FindControllerInLocalNetworkAsync(controllerHostName: string): Promise<string | null>;
	GetLastKnownControllerLocalIpAddress(): string | null;
	GetMdnsHostName(): string;
	GetControllerHostName(controllerSerial: string): string;
}

export class FindControllerService implements IFindControllerService, IDIService {
	private _connection: ExalusConnectionService;
	private _logger: ILoggerService;
	private _storage: LocalStorageService;
	private _localIpAddress: string | null = null;
	private _publicIpAddress: string | null = null;
	private _gatewayIpAddress: string | null = null;
	private _macAddress: string | null = null;
	private _isInLocalNetwork: boolean = false;
	public static readonly ServiceName: string = "FindControllerService";
	private readonly nativeFindControllerApi: IFindControllerInLocalNetwork | null = null;

	constructor() {
		if ((navigator as any).findEfcController !== undefined)
			this.nativeFindControllerApi = (navigator as any).findEfcController;

		this._connection = Api.Get<ExalusConnectionService>(ExalusConnectionService.ServiceName);
		this._logger = Api.Get<ILoggerService>(LoggerService.ServiceName);
		this._storage = Api.Get<LocalStorageService>(LocalStorageService.ServiceName);

		Api.Get<ISessionService>(SessionService.ServiceName).OnUserLoggedInEvent().Subscribe(async (user) => {
			try {
				await Task.Delay(5000);
				const res = await this.GetControllerNetworkConfigurationAsync();

				if (res != null) {
					this._localIpAddress = res.LocalIPv4;
					this._publicIpAddress = res.PublicIPv4;
					this._gatewayIpAddress = res.GatewayIPv4;
					this._macAddress = res.MAC;

					this._storage.Save(FindControllerService.ServiceName, "LocalIpAddress", this._localIpAddress);
					const controllerHostName = this.GetControllerHostName(this._connection.GetControllerSerialNumber()!);
					this._logger.Debug(`Looking for controller in local network with host name: ${controllerHostName}`);
					let r = null;

					if (Helpers.IsIosNative()) {
						r = await this.getIosControllerIpAsync(controllerHostName);
					}
					else {
						if (this.nativeFindControllerApi !== null) {
							r = await this.nativeFindControllerApi?.FindControllerAsync(controllerHostName);
						}
					}

					if (r != null && r != "") {
						this._isInLocalNetwork = true;
						this._logger.Debug(FindControllerService.ServiceName, `Controller found in local network with ip address: ${r}`);
					}
					else {
						this._isInLocalNetwork = false;
						this._logger.Debug(FindControllerService.ServiceName, `Controller not found in local network.`);
					}
				}
				else
					this._logger.Error(FindControllerService.ServiceName, `Failed to get network configuration data.`);
			}
			catch (e) {
				this._logger.Error(FindControllerService.ServiceName, `Failed to get network configuration data - ${e}`);
			}
		});
	}

	async FindControllerInLocalNetworkAsync(controllerHostName: string): Promise<string | null> {
		if (Helpers.IsIosNative()) {
			const r = await this.getIosControllerIpAsync(controllerHostName);

			if (r == "")
				return null;

			return r;
		}
		else {
			if (this.nativeFindControllerApi !== null)
				return this.nativeFindControllerApi.FindControllerAsync(controllerHostName);
			else
				return Promise.resolve(null);
		}
	}

	GetLastKnownControllerLocalIpAddress(): string | null {
		if (this._localIpAddress !== null)
			return this._localIpAddress;

		return this._storage.Read(FindControllerService.ServiceName, "LocalIpAddress");
	}

	public GetControllerHostName(controllerSerial: string): string {
		return `ExalusTR7-${controllerSerial}`;
	}

	public GetMdnsHostName(): string {
		return `ExalusTR7-${this.GetControllerHostName(this._connection.GetControllerSerialNumber() as string)}.local`.toLowerCase();
	}

	async GetControllerNetworkConfigurationAsync(): Promise<NetworkConfigurationResponse | null> {

		const result = await this._connection?.SendAndWaitForResponseAsync<NetworkConfigurationResponse>(new GetNetworkConfiguration(), 20000, false);

		if (result == null || result.Status == null || result.Data == null) {
			this._logger.Error(FindControllerService.ServiceName, `Failed to get network configuration data - unknow error.`);
			return null;
		}
		return result.Data;
	}

	GetServiceName(): string {
		return FindControllerService.ServiceName;
	}
	
	private getIosControllerIpAsync(hostname: string, timout = 20_000): Promise<string> {
		let timer: NodeJS.Timeout;

		return new Promise((resolve, reject) => {
			timer = setTimeout(() => {
				this._logger.Error(`Cannot find controller in local network, timeout!`);
				resolve("");
			}, timout);

			try {
				(window as any).nativeEvent.exalusFindController.handleFindController = (controllerAddress: string) => {
					clearTimeout(timer);
					resolve(controllerAddress);
				};

				(window as any).webkit.messageHandlers.exalusFindController.postMessage(hostname);
			} catch (err) {
				this._logger.Error(`Cannot find controller in local network, error: ${err}`);
				resolve("");
			}
		});
	}
}

class NetworkConfigurationResponse {
	Id: string = "";
	Name: string = "";
	LocalIPv4: string = "";
	MaskIPv4: string = "";
	GatewayIPv4: string = "";
	BroadcastIp: string = "";
	PublicIPv4: string = "";
	MAC: string = "";
	IsDhcpEnabled: boolean = false;
	InterfaceType: NetworkInterfaceType = NetworkInterfaceType.Unknown;
	OperationalStatus: OperationalStatus = OperationalStatus.Unknown;
	Speed: number = 0;
}

class GetNetworkConfiguration extends DataFrame<any> {
	constructor() {
		super();
		this.Resource = "/network/lan/";
		this.Method = Method.Get;
	}
}

declare enum NetworkInterfaceType {
	Unknown = 1,
	Ethernet = 6,
	TokenRing = 9,
	Fddi = 15,
	BasicIsdn = 20,
	PrimaryIsdn = 21,
	Ppp = 23,
	Loopback = 24,
	Ethernet3Megabit = 26,
	Slip = 28,
	Atm = 37,
	GenericModem = 48,
	FastEthernetT = 62,
	Isdn = 63,
	FastEthernetFx = 69,
	Wireless80211 = 71,
	AsymmetricDsl = 94,
	RateAdaptDsl = 95,
	SymmetricDsl = 96,
	VeryHighSpeedDsl = 97,
	IPOverAtm = 114,
	GigabitEthernet = 117,
	Tunnel = 131,
	MultiRateSymmetricDsl = 143,
	HighPerformanceSerialBus = 144,
	Wman = 237,
	Wwanpp = 243,
	Wwanpp2 = 244
}

declare enum OperationalStatus {
	Up = 1,
	Down = 2,
	Testing = 3,
	Unknown = 4,
	Dormant = 5,
	NotPresent = 6,
	LowerLayerDown = 7
}

interface IFindControllerInLocalNetwork {
	/// <summary>
	/// Finds controller in local network, returns ip address if controller is found in local network, otherwise returns null
	/// </summary>
	FindControllerAsync(controllerHostName: string): Promise<string | null>;
}